/**
 * Sandbox transpiler — compile and execute sandbox TypeScript files.
 *
 * This is the canonical compilation pipeline shared between the browser
 * sandbox runtime and the AI backend smoke-test tool. All transpilation,
 * module loading, and file-system virtualisation goes through here.
 */
import ts from 'typescript';
import { resolveModulePath } from './moduleResolver';
import { SANDBOX_COMPILER_OPTIONS } from './compilerConfig';
import type { CoverageTracker } from './coverage';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SandboxFileInput {
  name: string;
  code: string;
  errors?: string[];
}

export interface SandboxFolderInput {
  rootFiles: SandboxFileInput[];
  subfolders: Record<string, SandboxFileInput[]>;
}

export interface SandboxSystemsInput {
  client: SandboxFolderInput;
  server: SandboxFolderInput;
  shared: SandboxFolderInput;
}

export interface LoadResult {
  clientSystems: any[];
  serverSystems: any[];
  moduleCache: Map<string, any>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core compilation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transpile `code` and run it as a CommonJS module whose `require` is `requireFn` — the one evaluation step every
 * loader shares (`compileAndLoad` below, and the project-tree loader in `project/load.ts`). Throws what the module's
 * top level throws; the caller decides what a failure means. `options.compiled` is the module's JavaScript when the
 * caller has transpiled it already (the project loader does, to check its syntax first); it runs as given, never
 * instrumented — the v1 loader never passes it.
 */
export function evaluateModule(
  path: string,
  code: string,
  module: { exports: any },
  requireFn: (importPath: string) => unknown,
  sandboxConsole: Console,
  options?: { coverageTracker?: CoverageTracker; compiled?: string },
): void {
  // Instrument for coverage if tracker provided and this is a user file (not test).
  // The tracker does its own transpile internally (with a TS transformer that
  // inserts counters at valid statement positions only) so we skip the plain
  // transpileModule call when coverage is enabled.
  const tracker = options?.compiled === undefined ? options?.coverageTracker : undefined;
  const isTestFile = /\.(spec|test)\.ts$/.test(path) || path.includes('__tests__');
  const rawJsCode = options?.compiled ?? (tracker && !isTestFile
    ? tracker.instrument(path, code)
    : ts.transpileModule(code, { compilerOptions: { ...SANDBOX_COMPILER_OPTIONS } }).outputText);

  // Append `//# sourceURL=<path>` so V8 stack frames inside the
  // `new Function(...)` wrapper carry the original sandbox path rather
  // than `<anonymous>`. Without this, `runTestsNode`'s stack-trace
  // resolver (and any other downstream that wants to point the LLM at
  // a failing line) sees only opaque eval frames and can't surface the
  // source snippet — the new failure-context plumbing falls back to
  // "no file matched" and the LLM ends up needing an extra readFile
  // turn to locate the failure. Standard Chrome/V8 convention.
  // The sourceURL MUST start with `/` — the stack-frame resolver
  // (sandbox-test parseErrorFrames) only recognises virtual frames whose path
  // begins with a slash (`/[\S]+\.tsx?:line:col`). A bare `server/systems/X.ts`
  // sourceURL produces frames the resolver drops, so NO runtime log line and NO
  // error frame is ever captured for generated code (silently empties the fix
  // loop's runtime evidence). Normalise to exactly one leading slash.
  const sourceUrlPath = `/${path.replace(/^\/+/, '')}`;
  const jsCode = `${rawJsCode}\n//# sourceURL=${sourceUrlPath}`;

  const wrapper = tracker && !isTestFile
    ? new Function('require', 'module', 'exports', 'console', '__cov$', jsCode)
    : new Function('require', 'module', 'exports', 'console', jsCode);

  if (tracker && !isTestFile) {
    wrapper(requireFn, module, module.exports, sandboxConsole, tracker.global);
  } else {
    wrapper(requireFn, module, module.exports, sandboxConsole);
  }
}

/**
 * Compile and load a single module (and its dependencies) using a custom
 * CommonJS-style `require` implementation.
 *
 * @param entryFile      Absolute sandbox path, e.g. `/client/systems/InputSystem.ts`
 * @param virtualFiles   Map of all sandbox files: path → TypeScript source
 * @param moduleCache    Shared cache of already-loaded modules
 * @param externalModules Map of external package names to their runtime objects
 *                        (e.g. `@gotti/client` → the actual @gotti/client module)
 * @param sandboxConsole  Console to inject into sandboxed code (default: globalThis.console)
 * @param options         Optional settings
 * @param options.coverageTracker  When provided, instruments transpiled JS to collect
 *                                 line-level coverage counters.
 */
export function compileAndLoad(
  entryFile: string,
  virtualFiles: Map<string, string>,
  moduleCache: Map<string, any>,
  externalModules: Map<string, any>,
  sandboxConsole: Console = console,
  options?: { coverageTracker?: CoverageTracker },
): any {
  // Already loaded — return from cache
  if (moduleCache.has(entryFile)) {
    return moduleCache.get(entryFile);
  }

  // External package — return the real module, made CJS<->ESM-interop-safe.
  // User systems are transpiled with esModuleInterop, so a default import
  // (`import AssetManager from '@gotti/core'`) compiles to
  // __importDefault(require('@gotti/core')).default — which only resolves the
  // default export when the required object carries __esModule. The external
  // @gotti/* modules are ESM namespaces without that flag, so expose an
  // interop view that carries __esModule plus the namespace's exports. Named
  // exports and `default` are copied by reference, so singletons (e.g. the
  // shared AssetManager) stay identical across all systems.
  if (externalModules.has(entryFile)) {
    const mod = externalModules.get(entryFile);
    if (mod && mod.__esModule) return mod;
    return { ...mod, __esModule: true };
  }

  // Find source in virtual FS
  let code = virtualFiles.get(entryFile);
  if (!code && !entryFile.endsWith('.ts')) {
    const tsPath = entryFile + '.ts';
    if (virtualFiles.has(tsPath)) {
      code = virtualFiles.get(tsPath);
    }
  }

  if (!code) {
    sandboxConsole.warn(`[Sandbox] Module not found: ${entryFile}`);
    return {};
  }

  // Create module object and cache it BEFORE execution (handles circular refs)
  const module = { exports: {} as any };
  moduleCache.set(entryFile, module.exports);

  try {
    const requireFn = (importPath: string) => {
      const resolvedPath = resolveModulePath(entryFile, importPath);
      return compileAndLoad(resolvedPath, virtualFiles, moduleCache, externalModules, sandboxConsole, options);
    };
    evaluateModule(entryFile, code, module, requireFn, sandboxConsole, options);

    // Update cache with final exports (may have been reassigned via module.exports = ...)
    moduleCache.set(entryFile, module.exports);
  } catch (err) {
    sandboxConsole.error(`[Sandbox] Failed to compile ${entryFile}:`, err);
  }

  return module.exports;
}

// ─────────────────────────────────────────────────────────────────────────────
// System loading orchestration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all systems from the virtual file system and classify them as
 * client or server systems.
 *
 * @param files           Virtual FS map (path → source)
 * @param externalModules External package map (injected by the caller)
 * @param isClientSystem  Predicate: returns true if a class is a ClientSystem
 * @param isServerSystem  Predicate: returns true if a class is a ServerSystem
 * @param sandboxConsole  Console to inject into sandboxed code
 *
 * Load order (matches SandboxRuntime exactly):
 *   1. All files under `/shared/`
 *   2. Remaining non-system files (types, etc.)
 *   3. All files under `[prefix]/systems/`
 */
export function loadSystemsAndExports(
  files: Map<string, string>,
  externalModules: Map<string, any>,
  isClientSystem: (cls: any) => boolean,
  isServerSystem: (cls: any) => boolean,
  sandboxConsole: Console = console,
  /**
   * Optional pre-populated module cache to load INTO (and reuse from). Pass the
   * SAME cache the test runner used to compile the spec so the runtime systems
   * and the test spec share ONE instance of every `@/shared/*` module — without
   * this, a component class (e.g. `PlayerMovementComponent`) compiled separately
   * for the runtime and the spec are DIFFERENT identities, so a component the
   * spec attaches is invisible to `system.getComponent(SameNamedClass)` (and vice
   * versa) and authoritative movement silently no-ops. Defaults to a fresh cache
   * (the live preview / standalone boot, which has no spec to share with).
   */
  moduleCache: Map<string, any> = new Map<string, any>(),
): LoadResult {
  const clientSystems: any[] = [];
  const serverSystems: any[] = [];

  // Skip test files during production loading — they rely on @gotti/sandbox-test
  // which is only injected during explicit test execution.
  const isTest = (p: string) => /\.(spec|test)\.ts$/.test(p) || p.includes('__tests__');

  // 1. Shared files first (constants, types, components, entities)
  for (const [path] of files.entries()) {
    if (path.includes('/shared/') && !isTest(path)) {
      compileAndLoad(path, files, moduleCache, externalModules, sandboxConsole);
    }
  }

  // 2. Remaining non-system files (e.g. client/types.ts)
  for (const [path] of files.entries()) {
    if (!path.includes('/systems/') && !path.includes('/shared/') && !isTest(path)) {
      compileAndLoad(path, files, moduleCache, externalModules, sandboxConsole);
    }
  }

  // 3. System files — classify by path (ClientSystem/ServerSystem are type-only at runtime)
  //    Path-based detection is primary; predicates are fallback for non-standard layouts.
  for (const [path] of files.entries()) {
    if (path.includes('/systems/') && !isTest(path)) {
      const exports = compileAndLoad(path, files, moduleCache, externalModules, sandboxConsole);
      const isClientPath = path.includes('/client/');
      const isServerPath = path.includes('/server/');
      for (const key in exports) {
        const exportVal = exports[key];
        if (typeof exportVal === 'function') {
          if (isClientPath || isClientSystem(exportVal)) {
            clientSystems.push(exportVal);
          } else if (isServerPath || isServerSystem(exportVal)) {
            serverSystems.push(exportVal);
          }
        }
      }
    }
  }

  return { clientSystems, serverSystems, moduleCache };
}

// ─────────────────────────────────────────────────────────────────────────────
// Virtual file system builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a virtual file system (Map of path to source) from the structured
 * SandboxSystemsInput format. Files with errors are skipped (the frontend
 * and backend both skip errored files).
 */
export function buildVirtualFiles(systemsInput: SandboxSystemsInput): Map<string, string> {
  const virtualFiles = new Map<string, string>();

  const addFilesForFolder = (
    folderName: string,
    folder: SandboxFolderInput,
  ) => {
    for (const file of folder.rootFiles) {
      if (!file.errors || file.errors.length === 0) {
        virtualFiles.set(`/${folderName}/${file.name}.ts`, file.code);
      }
    }
    for (const [subfolderName, files] of Object.entries(folder.subfolders)) {
      for (const file of files) {
        if (!file.errors || file.errors.length === 0) {
          virtualFiles.set(`/${folderName}/${subfolderName}/${file.name}.ts`, file.code);
        }
      }
    }
  };

  addFilesForFolder('shared', systemsInput.shared);
  addFilesForFolder('client', systemsInput.client);
  addFilesForFolder('server', systemsInput.server);

  return virtualFiles;
}
